import {
  dayInZone,
  trackedRowNote,
  untrackedRowNote,
  GROUP_BY_VARIANT,
  LOGISTICS_ALERT_TYPES,
  type LogisticsAlertType,
} from '../../alerts/index.js';
import type { Delivery, InventoryLevel, UntrackedMaterial } from '../interfaces/ILogistics.js';
import { criticalOrderRefs, isOverdue } from './board.js';
import { lineNames } from './lines.js';

/**
 * The Deliveries & Stock alerts the Logistics page's own rows are currently
 * raising, grouped exactly as the dashboard's System Alerts card groups them.
 *
 * Same four groups, same note chain (`@d-orca/shared/alerts`), so the KPI tile
 * and the card can never disagree about how many materials are in trouble —
 * except Delivery late, which counts overdue orders (see the builder).
 *
 * Derived from the LIVE rows, not from stored alerts. The nightly sweep
 * de-duplicates — a note already announced does not re-announce — so a tile fed
 * from alert documents would read 3 the morning a material runs out and 0 the
 * next morning while it is still empty. The rows are the condition; the alerts
 * are the announcement of it.
 */
export interface ILogisticsAlertGroup {
  type: LogisticsAlertType;
  /** Worst severity among the group's rows. `stock-gap` is critical except on
   *  the tick day itself, where an order placed today still lands in time — the
   *  same downgrade the sweep applies (`logistics-detector.ts`). */
  severity: 'critical' | 'warning';
  /** Every material the group stands for, in row order. Feeds both the count
   *  and the `?q=` filter the tile hands to the panels. */
  materialNames: string[];
  /** The ORDERS the group stands for, when it counts orders rather than
   *  materials — Delivery late. Its count and its filter then go by these. */
  refs?: string[];
}

/** Groups whose every member is a warning. Anything else is critical unless a
 *  row downgrades it. */
const WARNING_TYPES: ReadonlySet<LogisticsAlertType> = new Set(['delivery-late', 'stock-untracked']);

/** Display order: critical band first, then warning, and within a band the
 *  fixed type order. Mirrors the alerts card's red-then-yellow banding. */
const TYPE_ORDER = new Map(LOGISTICS_ALERT_TYPES.map((type, index) => [type, index]));

/**
 * Delivery late is the one group NOT read off the rows: it counts the ORDERS
 * past their due date — the cards the board badges "Nd overdue". A row raises
 * one note, so a material that ran out while its order is late reads "Ran
 * out", and an untracked one "Not tracked"; counted by rows, "4 Delivery late"
 * could stand for two trucks while two other overdue trucks went missing from
 * its filter. The reason says "delivery", so it counts deliveries and filters
 * to exactly those cards. Every surface that shows these groups — the Logistics
 * KPI tile (web and mobile) and the dashboard's Deliveries & Stock card — goes
 * through here, so they cannot disagree about it.
 */
export function buildLogisticsAlertGroups(
  inventory: InventoryLevel[],
  untracked: UntrackedMaterial[],
  deliveries: Delivery[],
  tz: string,
  now: Date = new Date(),
): ILogisticsAlertGroup[] {
  const today = dayInZone(now, tz);
  const byType = new Map<LogisticsAlertType, ILogisticsAlertGroup>();

  const add = (type: LogisticsAlertType, materialName: string, severity: 'critical' | 'warning') => {
    // Delivery late comes from the orders below, not from the rows.
    if (type === 'delivery-late') return;
    const group = byType.get(type);
    if (group) {
      group.materialNames.push(materialName);
      if (severity === 'critical') group.severity = 'critical';
      return;
    }
    byType.set(type, { type, severity, materialNames: [materialName] });
  };

  for (const level of inventory) {
    const note = trackedRowNote(level, today, tz);
    if (note === null) continue;
    const type = GROUP_BY_VARIANT[note.variant];
    // The tick day is amber on the row, so it is amber in the count too.
    const severity = WARNING_TYPES.has(type) || note.variant === 'gap-today' ? 'warning' : 'critical';
    add(type, level.materialName, severity);
  }

  for (const material of untracked) {
    const note = untrackedRowNote(material, today);
    if (note === null) continue;
    add(GROUP_BY_VARIANT[note.variant], material.materialName, 'warning');
  }

  const late = deliveries.filter((delivery) => isOverdue(delivery, now, tz));
  if (late.length > 0) {
    byType.set('delivery-late', {
      type: 'delivery-late',
      severity: 'warning',
      materialNames: late.flatMap((delivery) => lineNames(delivery)),
      refs: late.map((delivery) => delivery.ref),
    });
  }

  return [...byType.values()].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
    return (TYPE_ORDER.get(a.type) ?? 0) - (TYPE_ORDER.get(b.type) ?? 0);
  });
}

/** How many things a group counts — its orders when it has them, else its
 *  materials. */
export function groupCount(group: ILogisticsAlertGroup): number {
  return group.refs ? group.refs.length : group.materialNames.length;
}

/**
 * The Materials search this group means: every one of its materials, quoted.
 *
 * Quoted because material names nest — an unquoted `Waterproof sheet` also
 * matches `Demo Waterproof Sheet`, so a filter meaning "these three rows" would
 * quietly land on four (`materialsSearch.ts`). Identical to the query the
 * alerts card's "View in Logistics ›" link builds, so arriving from the card
 * and clicking the tile land on exactly the same rows.
 */
export function groupQuery(group: ILogisticsAlertGroup): string {
  // Refs quote the same way: an exact ref lands on its own card, never on a
  // longer ref that contains it.
  return namesQuery(group.refs ?? group.materialNames);
}

/** The same quoted query for any list of material names — the Delivered-not-
 *  synced tile filters through it too, so every tile lands on rows the same
 *  way. */
export function namesQuery(materialNames: string[]): string {
  return [...new Set(materialNames)].map((name) => `"${name}"`).join(', ');
}

/** The search once `toggled` is starred or unstarred: while the Critical orders
 *  tile is the filter it follows the new set, so an unstarred order leaves the
 *  list and the last one clears the filter. Any other search stands. */
export function queryAfterCriticalToggle(deliveries: Delivery[], toggled: Delivery, query: string): string {
  // Nothing starred yet: an empty search only looks like the (empty) Critical filter.
  if (query === '' || query !== namesQuery(criticalOrderRefs(deliveries))) return query;
  const next = deliveries.map((order) => (order.ref === toggled.ref ? { ...order, isCritical: !toggled.isCritical } : order));
  return namesQuery(criticalOrderRefs(next));
}
